import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useMetricsData } from './hooks/useMetricsData';
import { TimeToolbar } from './components/TimeToolbar';
import { MetricSelector } from './components/MetricSelector';
import { PlotlyChart } from './components/PlotlyChart';
import { StatsPanel } from './components/StatsPanel';
import { ChartSettingsModal } from './components/ChartSettings';
import { ExportPanel } from './components/ExportPanel';
import { BatchAssignModal, BatchAssignmentsList } from './components/BatchAssign';
import { ChartSettings, DEFAULT_CHART_SETTINGS, ChartAnnotation, ThresholdLine, BatchOverlay, METRIC_COLORS } from './types';
import dataApi, { BatchAssignment } from '../../../Api/data.api';

interface MetricsExplorerProps {
  datasetId: number;
  datasetName: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    padding: '1rem 0',
  },
  mainContent: {
    display: 'flex',
    gap: '1rem',
  },
  chartColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  sidePanel: {
    width: '280px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  sidePanelCollapsed: {
    width: '40px',
    flexShrink: 0,
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
  },
  actionButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#c5c5c5',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  toggleButton: {
    padding: '0.4rem',
    borderRadius: '4px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.6)',
    color: '#8b949e',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorBanner: {
    padding: '1rem',
    background: 'rgba(255, 107, 107, 0.1)',
    border: '1px solid rgba(255, 107, 107, 0.3)',
    borderRadius: '8px',
    color: '#ff6b6b',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: '#6c757d',
    textAlign: 'center' as const,
    background: 'rgba(33, 40, 44, 0.6)',
    borderRadius: '8px',
    border: '1px solid rgba(108, 117, 125, 0.2)',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: '1.1rem',
    color: '#c5c5c5',
    marginBottom: '0.5rem',
  },
  emptyDesc: {
    fontSize: '0.9rem',
    maxWidth: '400px',
  },
};

export function MetricsExplorer({ datasetId, datasetName }: MetricsExplorerProps) {
  const plotRef = useRef<any>(null);

  // Data hook
  const {
    data,
    dataExtent,
    stats,
    totalCount,
    loading,
    error,
    lastUpdated,
    timePreset,
    setTimePreset,
    customFrom,
    customTo,
    setCustomRange,
    aggregation,
    setAggregation,
    effectiveAggregation,
    isZoomed,
    selectedMetrics,
    toggleMetric,
    selectAllMetrics,
    deselectAllMetrics,
    liveMode,
    setLiveMode,
    refresh,
    fetchForZoomRange,
    resetToPreset,
  } = useMetricsData({ datasetId });

  // Local UI state
  const [chartSettings, setChartSettings] = useState<ChartSettings>(DEFAULT_CHART_SETTINGS);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(false);
  const [statsPanelCollapsed, setStatsPanelCollapsed] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [annotations, setAnnotations] = useState<ChartAnnotation[]>([]);
  const [thresholds, setThresholds] = useState<ThresholdLine[]>([]);
  const [visibleRange, setVisibleRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [resetRangeTrigger, setResetRangeTrigger] = useState(0);

  // Batch-assignment state
  const [assignMode, setAssignMode] = useState(false);
  const [assignments, setAssignments] = useState<BatchAssignment[]>([]);
  const [pendingRange, setPendingRange] = useState<{ from: Date; to: Date } | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [assignNotice, setAssignNotice] = useState<string | null>(null);

  // Load existing batch assignments for this dataset
  const loadAssignments = useCallback(async () => {
    try {
      const result = await dataApi.getBatchAssignments(datasetId);
      setAssignments(result);
    } catch (err) {
      console.error('Failed to load batch assignments:', err);
    }
  }, [datasetId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Stable color per assignment id (matches chart overlay + list swatch)
  const colorFor = useCallback(
    (id: number) => {
      const idx = assignments.findIndex(a => a.id === id);
      return METRIC_COLORS[(idx < 0 ? 0 : idx) % METRIC_COLORS.length];
    },
    [assignments]
  );

  // Only show overlays for assignments that overlap the loaded time window, and
  // clamp their edges to the data extent so out-of-window assignments don't
  // stretch the x-axis (Plotly autorange includes shape x-coordinates).
  const batchOverlays: BatchOverlay[] = useMemo(() => {
    if (!dataExtent) return [];
    const fromMs = dataExtent.from.getTime();
    const toMs = dataExtent.to.getTime();

    return assignments
      .filter(a => {
        const aFrom = new Date(a.from).getTime();
        const aTo = new Date(a.to).getTime();
        return aTo >= fromMs && aFrom <= toMs;
      })
      .map(a => {
        const aFrom = Math.max(new Date(a.from).getTime(), fromMs);
        const aTo = Math.min(new Date(a.to).getTime(), toMs);
        return {
          id: a.id,
          from: new Date(aFrom).toISOString(),
          to: new Date(aTo).toISOString(),
          color: colorFor(a.id),
          label: a.batch ? `#${a.batch.sequence} ${a.batch.mixCode}` : `Batch ${a.batchId}`,
        };
      });
  }, [assignments, dataExtent, colorFor]);

  const handleRangeSelect = useCallback((from: Date, to: Date) => {
    setPendingRange({ from, to });
  }, []);

  const handleAssignmentSaved = useCallback(async (metricCount: number) => {
    setPendingRange(null);
    setAssignNotice(`Assigned ${metricCount} metrics to batch.`);
    await loadAssignments();
    refresh();
    setTimeout(() => setAssignNotice(null), 4000);
  }, [loadAssignments, refresh]);

  const handleDeleteAssignment = useCallback(async (id: number) => {
    setDeletingId(id);
    try {
      await dataApi.deleteBatchAssignment(datasetId, id);
      await loadAssignments();
      refresh();
    } catch (err) {
      console.error('Failed to delete assignment:', err);
    } finally {
      setDeletingId(null);
    }
  }, [datasetId, loadAssignments, refresh]);

  // Trigger chart resize when side panel state changes
  useEffect(() => {
    setResizeTrigger(prev => prev + 1);
  }, [sidePanelCollapsed]);

  // Trigger range reset when time preset changes (not when zooming)
  useEffect(() => {
    setResetRangeTrigger(prev => prev + 1);
  }, [timePreset]);

  // Handlers
  const handleChartSettingsChange = useCallback((updates: Partial<ChartSettings>) => {
    setChartSettings(prev => ({ ...prev, ...updates }));
  }, []);

  // Ref for debouncing zoom fetch
  const zoomFetchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle zoom changes - fetch new data for the zoomed range
  // Rangeslider reset is separate (only triggered by time filter buttons via resetRangeTrigger)
  const handleZoomChange = useCallback((from: Date | null, to: Date | null) => {
    setVisibleRange({ from, to });

    // Debounce the data fetch
    if (zoomFetchTimerRef.current) {
      clearTimeout(zoomFetchTimerRef.current);
    }

    if (from && to) {
      // Fetch data for the zoomed range (higher resolution)
      zoomFetchTimerRef.current = setTimeout(() => {
        fetchForZoomRange(from, to);
      }, 300);
    }
  }, [fetchForZoomRange]);

  const handleAddAnnotation = useCallback((timestamp: string, value: number) => {
    const newAnnotation: ChartAnnotation = {
      id: `ann_${Date.now()}`,
      timestamp,
      label: `Mark ${annotations.length + 1}`,
      color: '#ff9800',
      type: 'vertical',
    };
    setAnnotations(prev => [...prev, newAnnotation]);
  }, [annotations.length]);

  // Show empty state if no data and not loading
  if (!loading && data.length === 0 && !error) {
    return (
      <div style={styles.container}>
        <TimeToolbar
          timePreset={timePreset}
          onTimePresetChange={setTimePreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomRangeChange={setCustomRange}
          aggregation={aggregation}
          onAggregationChange={setAggregation}
          effectiveAggregation={effectiveAggregation}
          liveMode={liveMode}
          onLiveModeChange={setLiveMode}
          lastUpdated={lastUpdated}
          totalCount={totalCount}
          dataPointCount={data.length}
          isZoomed={isZoomed}
          onResetZoom={resetToPreset}
        />
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📊</div>
          <div style={styles.emptyTitle}>No Metrics Data</div>
          <div style={styles.emptyDesc}>
            This dataset doesn't have any metrics recorded yet, or there's no data in the selected time range.
            Try selecting a different time range or wait for data to be collected.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Keyframes for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Error banner */}
      {error && (
        <div style={styles.errorBanner}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          {error}
          <button
            onClick={refresh}
            style={{ ...styles.actionButton, marginLeft: 'auto' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Time controls toolbar */}
      <TimeToolbar
        timePreset={timePreset}
        onTimePresetChange={setTimePreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomRangeChange={setCustomRange}
        aggregation={aggregation}
        onAggregationChange={setAggregation}
        effectiveAggregation={effectiveAggregation}
        liveMode={liveMode}
        onLiveModeChange={setLiveMode}
        lastUpdated={lastUpdated}
        totalCount={totalCount}
        dataPointCount={data.length}
        isZoomed={isZoomed}
        onResetZoom={resetToPreset}
      />

      {/* Action bar */}
      <div style={styles.actionBar}>
        <div style={styles.actionButtons}>
          <button
            style={styles.actionButton}
            onClick={() => setShowSettingsModal(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Settings
          </button>
          <button
            style={styles.actionButton}
            onClick={() => setShowExportModal(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Export
          </button>
          <button
            style={{
              ...styles.actionButton,
              ...(assignMode
                ? {
                    background: 'rgba(0, 255, 136, 0.15)',
                    borderColor: 'rgba(0, 255, 136, 0.45)',
                    color: '#00ff88',
                  }
                : {}),
            }}
            onClick={() => setAssignMode(prev => !prev)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            {assignMode ? 'Assigning…' : 'Assign batch'}
          </button>
          <button
            style={{
              ...styles.actionButton,
              background: 'rgba(83, 189, 228, 0.15)',
              borderColor: 'rgba(83, 189, 228, 0.4)',
              color: '#53bde4',
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onClick={refresh}
            disabled={loading}
          >
            {loading ? (
              <span
                style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(83, 189, 228, 0.3)',
                  borderTopColor: '#53bde4',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            )}
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Batch assign info banner */}
      {assignMode && (
        <div
          style={{
            padding: '0.6rem 0.85rem',
            background: 'rgba(0, 255, 136, 0.08)',
            border: '1px solid rgba(0, 255, 136, 0.3)',
            borderRadius: '8px',
            color: '#00ff88',
            fontSize: '0.82rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3v18h18" />
            <rect x="7" y="9" width="4" height="9" />
            <rect x="14" y="5" width="4" height="13" />
          </svg>
          Drag across the chart to select a time period, then pick a batch to assign it to.
          {assignNotice && <span style={{ marginLeft: 'auto', color: '#c5c5c5' }}>{assignNotice}</span>}
        </div>
      )}

      {/* Statistics panel - full width above chart, collapsible */}
      <StatsPanel
        selectedMetrics={selectedMetrics}
        stats={stats}
        visibleRange={visibleRange}
        collapsed={statsPanelCollapsed}
        onToggleCollapse={() => setStatsPanelCollapsed(!statsPanelCollapsed)}
      />

      {/* Main content area */}
      <div style={styles.mainContent}>
        {/* Chart column */}
        <div style={styles.chartColumn}>
          <PlotlyChart
            data={data}
            dataExtent={dataExtent}
            selectedMetrics={selectedMetrics}
            chartSettings={chartSettings}
            annotations={annotations}
            thresholds={thresholds}
            batchOverlays={batchOverlays}
            selectionMode={assignMode}
            onRangeSelect={handleRangeSelect}
            onZoomChange={handleZoomChange}
            onAddAnnotation={handleAddAnnotation}
            height={700}
            resizeTrigger={resizeTrigger}
            resetRangeTrigger={resetRangeTrigger}
          />
          {assignMode && (
            <BatchAssignmentsList
              assignments={assignments}
              colorFor={colorFor}
              onDelete={handleDeleteAssignment}
              deletingId={deletingId}
            />
          )}
        </div>

        {/* Side panel - metrics only */}
        <div style={sidePanelCollapsed ? styles.sidePanelCollapsed : styles.sidePanel}>
          {sidePanelCollapsed ? (
            <button
              style={{ ...styles.toggleButton, width: '100%', height: '100%' }}
              onClick={() => setSidePanelCollapsed(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          ) : (
            <MetricSelector
              metrics={selectedMetrics}
              stats={stats}
              onToggleMetric={toggleMetric}
              onSelectAll={selectAllMetrics}
              onDeselectAll={deselectAllMetrics}
              onHidePanel={() => setSidePanelCollapsed(true)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <ChartSettingsModal
        settings={chartSettings}
        onSettingsChange={handleChartSettingsChange}
        visible={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <ExportPanel
        data={data}
        selectedMetrics={selectedMetrics}
        stats={stats}
        datasetName={datasetName}
        visible={showExportModal}
        onClose={() => setShowExportModal(false)}
        plotRef={plotRef}
      />

      {pendingRange && (
        <BatchAssignModal
          datasetId={datasetId}
          range={pendingRange}
          onClose={() => setPendingRange(null)}
          onSaved={handleAssignmentSaved}
        />
      )}
    </div>
  );
}

export default MetricsExplorer;
